import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import WhiteboardButton from './WhiteboardButton';
import KnockButton from './KnockButton';
import GroupsButton from './GroupsButton';
import TileViewButton from './TileViewButton';
import SettingsButton from './SettingsButton';
import BrowserButton from './BrowserButton';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import {
  setHangupMenuVisible,
  setOverflowMenuVisible,
  setToolbarHovered,
  showToolbox
} from 'store/modules/toolbox/actions';
import { translate } from 'store/modules/base/i18n';
import { connect } from 'store/modules/base/redux';
import { getRemoteParticipantCount } from 'store/modules/base/participants';

const styles = () => {
  return {
    contextMenu: {
      position: 'relative',
      right: 'auto',
      maxHeight: 'inherit',
      margin: 0,
      marginBottom: '8px'
    },

    hangupMenu: {
      position: 'relative',
      right: 'auto',
      display: 'flex',
      flexDirection: 'column',
      rowGap: '8px',
      margin: 0,
      padding: '16px',
      marginBottom: '8px'
    }
  };
};

/**
 * Implements the conference toolbox on React/Web.
 *
 * @augments Component
 */
class ToolboxWeb extends Component {
  /**
   * Initializes a new {@code Toolbox} instance.
   *
   * @param {IProps} props - The read-only React {@code Component} props with
   * which the new instance is to be initialized.
   */
  constructor(props) {
    super(props);
    this._onEscKey = this._onEscKey.bind(this);
    this._onTabIn = this._onTabIn.bind(this);
    this.state = { buttons: [] };
  }

  /**
   * Sets keyboard shortcuts for to trigger ToolbarButtons actions.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this._getAllButtons();
  }

  /**
   * Update the visibility of the {@code OverflowMenuButton}.
   *
   * @inheritdoc
   */
  componentDidUpdate(prevProps) {
    const { _dialog, _visible, dispatch } = this.props;
    if (prevProps._overflowMenuVisible && !prevProps._dialog && _dialog) {
      this._onSetOverflowVisible(false);
      dispatch(setToolbarHovered(false));
    }
    if (prevProps._hangupMenuVisible && prevProps._visible && !_visible) {
      this._onSetHangupVisible(false);
      dispatch(setToolbarHovered(false));
    }
    this._getAllButtons();
  }

  /**
   * Removes keyboard shortcuts registered by this component.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {}

  /**
   * Returns all buttons that could be rendered.
   *
   * @param {Object} state - The redux state.
   * @returns {Object} The button maps mainMenuButtons and overflowMenuButtons.
   */
  _getAllButtons() {
    const { _user, _devices } = this.props;
    let buttons = [
      <SettingsButton showLabel={false} key="settings" id="menu-button" />,
      _user.device?.options?.showViewButton === 1 && _devices > 1 && (
        <TileViewButton showLabel={false} key="tileview" />
      ),
      _user.device?.groups?.length > 1 && (
        <GroupsButton showLabel={false} key="groups" />
      ),
      _user.device?.options?.showKnockButton === 1 && (
        <KnockButton showLabel={false} key="knock" />
      ),
      _user.device?.options?.showWhiteboardButton === 1 && (
        <WhiteboardButton showLabel={false} key="whitebord" />
      )
    ].filter((val) => Boolean(val));
    if (
      _user.device?.allowBrowser &&
      _user.device?.browserOptions?.length > 0
    ) {
      _user.device?.browserOptions.map((item, index) => {
        buttons.push(
          <BrowserButton key={index} options={item} showLabel={false} />
        );
      });
    }
    buttons = buttons.filter((val) => Boolean(val));
    if (buttons.length !== this.state.buttons.length)
      this.setState({ buttons });
  }

  /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
  render() {
    if (this.props._disabled) {
      return null;
    }

    const { _chatOpen, _visible } = this.props;
    const rootClassNames = `new-toolbox-web ${_visible ? 'visible' : ''} ${
      _chatOpen ? 'shift-right' : ''
    }`;

    return (
      <div className={rootClassNames} id="new-toolbox-web">
        <div className="toolbox-content-web">
          {this.state.buttons.map((item) => item)}
          {/* <div style={{ marginTop: '80px' }}>
            {this.state.buttons
              .slice(
                Math.ceil(this.state.buttons.length / 2),
                this.state.buttons.length
              )
              .map((item) => item)}
          </div> */}
        </div>
      </div>
    );
  }

  /**
   * Key handler for overflow/hangup menus.
   *
   * @param {KeyboardEvent} e - Esc key click to close the popup.
   * @returns {void}
   */
  _onEscKey(e) {
    if (e?.key === 'Escape') {
      e?.stopPropagation();
      this._closeHangupMenuIfOpen();
      this._closeOverflowMenuIfOpen();
    }
  }

  /**
   * Closes the hangup menu if opened.
   *
   * @private
   * @returns {void}
   */
  _closeHangupMenuIfOpen() {
    const { dispatch, _hangupMenuVisible } = this.props;

    _hangupMenuVisible && dispatch(setHangupMenuVisible(false));
  }

  /**
   * Closes the overflow menu if opened.
   *
   * @private
   * @returns {void}
   */
  _closeOverflowMenuIfOpen() {
    const { dispatch, _overflowMenuVisible } = this.props;

    _overflowMenuVisible && dispatch(setOverflowMenuVisible(false));
  }

  /**
   * Sets the visibility of the hangup menu.
   *
   * @param {boolean} visible - Whether or not the hangup menu should be
   * displayed.
   * @private
   * @returns {void}
   */
  _onSetHangupVisible(visible) {
    this.props.dispatch(setHangupMenuVisible(visible));
    this.props.dispatch(setToolbarHovered(visible));
  }

  /**
   * Sets the visibility of the overflow menu.
   *
   * @param {boolean} visible - Whether or not the overflow menu should be
   * displayed.
   * @private
   * @returns {void}
   */
  _onSetOverflowVisible(visible) {
    this.props.dispatch(setOverflowMenuVisible(visible));
    this.props.dispatch(setToolbarHovered(visible));
  }

  /**
   * Toggle the toolbar visibility when tabbing into it.
   *
   * @returns {void}
   */
  _onTabIn() {
    if (!this.props._visible) {
      this.props.dispatch(showToolbox());
    }
  }
}

/**
 * Maps (parts of) the redux state to {@link Toolbox}'s React {@code Component}
 * props.
 *
 * @param {Object} state - The redux store/state.
 * @param {Object} ownProps - The props explicitly passed.
 * @private
 * @returns {{}}
 */
function _mapStateToProps(state, props) {
  const { user } = state['features/login'];
  const { iAmRecorder, iAmSipGateway } = state['features/base/config'];
  const { overflowMenuVisible, overflowDrawer } = state['features/toolbox'];
  const { clientWidth, clientHeight } = state['features/base/responsive-ui'];

  return {
    _user: user,
    _devices: getRemoteParticipantCount(state),
    _chatOpen: state['features/chat'].isOpen,
    _clientWidth: clientWidth,
    _dialog: Boolean(state['features/base/dialog'].component),
    _disabled: Boolean(iAmRecorder || iAmSipGateway),
    _overflowMenuVisible: overflowMenuVisible,
    _overflowDrawer: overflowDrawer,
    _visible: clientWidth > 500 && clientHeight > 500 && props.visible
  };
}
ToolboxWeb.propTypes = {
  _user: PropTypes.object,
  _devices: PropTypes.any,
  _buttonsWithNotifyClick: PropTypes.func,
  _chatOpen: PropTypes.bool,
  _clientWidth: PropTypes.number,
  _dialog: PropTypes.bool,
  _disabled: PropTypes.bool,
  _hangupMenuVisible: PropTypes.bool,
  _overflowMenuVisible: PropTypes.bool,
  _jwtDisabledButons: PropTypes.array,
  _toolbarButtons: PropTypes.array,
  _visible: PropTypes.bool,
  dispatch: PropTypes.func
};
export default React.memo(
  translate(
    connect(_mapStateToProps)(withStyles(styles)(withBoundary(ToolboxWeb)))
  )
);
